/**
 * Question type configuration for assessments.
 * Maps API custom question types to internal component types and default props.
 * Keep in sync with API assessmentConstants.QUESTION_TYPES.
 */

/** Custom question types (API and UI). Keep in sync with API assessmentConstants.QUESTION_TYPES. */
export const QUESTION_TYPES = [
  'text',
  'textarea',
  'number',
  'single_select',
  'multiple_select',
  'date',
  'date_of_birth',
];

/**
 * Reserved component types (system-only, not in CUSTOM_QUESTION_TYPE_TO_COMPONENT):
 * - SliderSelect: Weight training experience slider with marks/tooltips (min/max/step).
 *   Used by questionKey 'weightTraningexprience'. Not available as a custom question type.
 * - MacroSelect: Macros pie chart (foodPreference-driven macro ratio selection).
 *   Used by questionKey 'macros'. Required for BMR/macro calculation; not available as custom.
 */

/** Internal component type strings the renderer supports (registry keys). */
export const COMPONENT_TYPES = [
  'Input',
  'TextArea',
  'Tabs',
  'DatePicker',
  'DatePickerBirthday',
  'CustomCheckbox',
  'SliderSelect',
  'MacroSelect',
  'weightTraningexprience', // alias for SliderSelect
  'CustomPieChart', // alias for MacroSelect
  'HeightFtAndIc',
  'InputAutoCompleteMap',
  'newPassword',
  'OptionsOnly', // questions with options but no component (e.g. gender, goal, single_select)
];

/**
 * Map API custom question type to internal component type and default props.
 * Used by buildComponentFromCustom to produce the shape AssessmentQuestions expects.
 */
export const CUSTOM_QUESTION_TYPE_TO_COMPONENT = {
  text: {
    componentType: 'Input',
    defaults: {
      inputType: 'text',
      placeholder: 'Type here...',
    },
  },
  textarea: {
    componentType: 'TextArea',
    defaults: {
      placeholder: 'Type here...',
    },
  },
  number: {
    componentType: 'Input',
    defaults: {
      inputType: 'number',
      placeholder: 'Enter number',
    },
  },
  single_select: {
    componentType: 'OptionsOnly',
    defaults: {},
    // Options are attached on the question (no component); rendered via options-only path.
  },
  multiple_select: {
    componentType: 'CustomCheckbox',
    defaults: {},
    // Options go on render[0].options
  },
  date: {
    componentType: 'DatePicker',
    defaults: {},
  },
  date_of_birth: {
    componentType: 'DatePickerBirthday',
    defaults: {},
  },
};

/**
 * Normalize API options [{ value, label }] to component shape [{ key, text, value }].
 * @param {Array<{ value: string, label?: string }>} apiOptions
 * @returns {Array<{ key: string, text: string, value: string }>}
 */
export function normalizeOptionsForComponent(apiOptions) {
  if (!Array.isArray(apiOptions)) return [];
  return apiOptions.map((opt) => ({
    key: opt.value != null ? String(opt.value) : '',
    text: (opt.label != null ? opt.label : opt.value) != null ? String(opt.label || opt.value) : '',
    value: opt.value != null ? String(opt.value) : '',
  })).filter((o) => o.value !== '' || o.text !== '');
}
